import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, Platform, Text } from 'react-native';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { DLODashboardTitle } from '../../components/DLOTitle';
import DLOBreadcrumb from '../../components/DLOBreadcrumb';
import DLOModal from '../../components/DLOModal';

// Local imports
import { useDLOLocationData } from './hooks/useDLOLocationData';
import { useDLOLocationHandlers } from './hooks/useDLOLocationHandlers';
import { createBreadcrumbItems } from './constants';
import { DLOLocationSettingProps, ModalState } from './types';
import DSDVolumeTables from './components/DSDVolumeTables';
import NONDSDVolumeTables from './components/NONDSDVolumeTables';
import ScheduleTables from './components/ScheduleTables';

const DLOLocationSetting: React.FC<DLOLocationSettingProps> = ({ onNavigate }) => {
  // State for modal
  const [modalState, setModalState] = useState<ModalState>({
    visible: false,
    title: '',
    message: '',
    type: 'success'
  });

  // Use custom hooks
  const { dloLocationSetting, historicalHoursSaved, historicalHoursSaving } = useDLOLocationData();
  
  // Get selected location from Redux
  const selectedLocation = useSelector((state: RootState) => state.appData.selectedLocation);
  
  // Initialize handlers with required props
  const handlers = useDLOLocationHandlers({
    selectedLocation,
    setModalState,
  });

  // Generate breadcrumb items
  const breadcrumbItems = createBreadcrumbItems(onNavigate);

  // Handle modal close
  const handleModalClose = () => {
    setModalState(prev => ({ ...prev, visible: false }));
  };

  return (
    <ScrollView style={styles.container}>
      {/* Breadcrumb Navigation */}
      <View style={styles.breadcrumbContainer}>
        <DLOBreadcrumb 
          items={breadcrumbItems}
          showHomeIcon={true}
          onHomePress={() => onNavigate?.('Home')}
        />
      </View>

      {/* Page Title */}
      <View style={styles.headerContainer}>
        <DLODashboardTitle
          title="Location Settings"
          // subtitle="Configure and manage location-specific settings for labor optimization"
        />
      </View>

      {/* Page Content - Show tables as they load individually */}
      <View style={styles.content}>
        
        {/* DSD Volume Tables */}
        <DSDVolumeTables 
          dloLocationSetting={dloLocationSetting}
          styles={styles}
        />

        {/* NON-DSD Volume Tables */}
        <NONDSDVolumeTables 
          dloLocationSetting={dloLocationSetting}
          styles={styles}
        />

        {/* Schedule Tables (Historical Hours & Shift Times) */}
        <ScheduleTables 
          dloLocationSetting={dloLocationSetting}
          handlers={handlers}
          historicalHoursSaved={historicalHoursSaved}
          historicalHoursSaving={historicalHoursSaving}
          styles={styles}
        />
      </View>

      {/* Modal for success/error messages */}
      <DLOModal
        visible={modalState.visible}
        onClose={handleModalClose}
        title={modalState.title}
        primaryButtonText="OK"
        secondaryButtonText=""
        onPrimaryPress={handleModalClose}
        hideButtons={false}
        showCloseIcon={true}
      >
        <Text style={styles.modalMessage}>{modalState.message}</Text>
      </DLOModal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 50,
    backgroundColor: 'rgb(240, 245, 252)',
  },
  breadcrumbContainer: {
    backgroundColor: 'rgb(240, 245, 252)',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  headerContainer: {
    backgroundColor: 'rgb(240, 245, 252)',
    paddingVertical: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  content: {
    flex: 1,
    backgroundColor: 'rgb(240, 245, 252)',
    marginBottom: 20,
  },
  contentText: {
    fontSize: 18,
    color: '#34495e',
    marginBottom: 20,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  settingsSection: {
    marginBottom: 30,
    padding: 15,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#495057',
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#6c757d',
    lineHeight: 20,
    marginBottom: 16,
  },
  table: {
    marginTop: 20,
  },
  scheduleTable: {
    marginTop: 20,
  },
  volumeTable: {
    marginTop: 20,
  },
  percentageTable: {
    marginTop: 20,
  },
  zoneDistributionTable: {
    marginTop: 20,
  },
  tableLoaderContainer: {
    marginTop: 20,
    padding: 40,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  tableLoadingText: {
    marginTop: 16,
    fontSize: 14,
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    textAlign: 'center',
  },
  modalMessage: {
    fontSize: 16,
    color: '#3A3A3A',
    lineHeight: 24,
    textAlign: 'center',
    marginVertical: 10,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
});

export default DLOLocationSetting;
